import React, { useEffect, useState } from "react";
import { getInstitutionDetails, updateInstitutionDetails } from "../../services/institution";
import { useAuth } from "../../context/AuthContext";
import { Skeleton } from "@mui/material";

export default function InstitutionSettingsPage() {
    const { institution, token } = useAuth();
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [details, setDetails] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        socials: [],
        image: null,
    });

    useEffect(() => {
        if (institution?.id) fetchDetails();
    }, [institution]);

    const fetchDetails = async () => {
        try {
            const res = await getInstitutionDetails(institution.id);
            setDetails(res.data);
            setFormData({
                name: res.data.name || "",
                socials: res.data.contactInfo || [],
                image: res.data.image || null,
            });
        } catch (err) {
            console.error("فشل تحميل البيانات");
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData((prev) => ({ ...prev, image: file }));
        }
    };

    const handleSocialChange = (index, value) => {
        setFormData((prev) => {
            const updated = [...prev.socials];
            updated[index] = { url: value };
            return { ...prev, socials: updated };
        });
    };

    const addSocial = () => {
        setFormData((prev) => ({
            ...prev,
            socials: [...prev.socials, { url: "" }],
        }));
    };

    const removeSocial = (index) => {
        setFormData((prev) => {
            const updated = [...prev.socials];
            updated.splice(index, 1);
            return { ...prev, socials: updated };
        });
    };

    const handleSave = async () => {
        setIsLoading(true);
        try {
            const formattedContactInfo = formData.socials
                .filter((item) => item.url?.trim())
                .map((item) => ({ url: item.url }));

            const form = new FormData();
            form.append("name", formData.name);
            form.append("contactInfo", JSON.stringify(formattedContactInfo));
            if (formData.image instanceof File) {
                form.append("image", formData.image);
            }

            await updateInstitutionDetails(institution.id, form, token);
            setIsEditing(false);
            fetchDetails();
        } catch (err) {
            console.error("فشل الحفظ", err);
        } finally {
            setIsLoading(false);
        }
    };

    if (!details) {
        return (
            <div className="page">
                <div className="content-view">
                    <div className="profile-header">
                        <div className="info">
                            <div className="profile-image-container">
                                <Skeleton variant="circular" width={80} height={80} />
                            </div>
                            <div style={{ marginRight: 20, flex: 1 }}>
                                <Skeleton variant="text" width="50%" height={28} />
                                <Skeleton variant="text" width="30%" height={24} />
                            </div>
                        </div>
                    </div>

                    <div className="profile-info" style={{ marginTop: 30 }}>
                        <div className="info-row">
                            <span className="label">اسم المؤسسة</span>
                            <Skeleton variant="rectangular" width="100%" height={40} style={{ borderRadius: 8 }} />
                        </div>

                        <div className="info-row" style={{ marginTop: 20 }}>
                            <span className="label">روابط التواصل الاجتماعي</span>
                            {[1, 2, 3].map((_, idx) => (
                                <Skeleton
                                    key={idx}
                                    variant="rectangular"
                                    width="100%"
                                    height={36}
                                    style={{ borderRadius: 6, marginBottom: 12 }}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="profile-actions" style={{ marginTop: 30 }}>
                        <Skeleton variant="rectangular" width={100} height={40} style={{ borderRadius: 8 }} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page">
            <div className="content-view">
                <div className="profile-header">
                    <div className="info">
                        <div className="profile-image-container">
                            <img
                                src={
                                    formData.image instanceof File
                                        ? URL.createObjectURL(formData.image)
                                        : formData.image || "/assets/default.jpg"
                                }
                                alt="صورة المؤسسة"
                                className="profile-image"
                            />
                            {isEditing && (
                                <label className="edit-icon">
                                    ✎
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        style={{ display: "none" }}
                                    />
                                </label>
                            )}
                        </div>
                        <div>
                            <h3>{formData.name}</h3>
                        </div>
                    </div>
                </div>

                <div className="profile-info">
                    <div className="info-row">
                        <span className="label">اسم المؤسسة</span>
                        <div style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
                            {isEditing ? (
                                <input
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="input"
                                />
                            ) : (
                                <span className="value">{formData.name}</span>
                            )}
                        </div>
                    </div>

                    <div className="info-row">
                        <span className="label">روابط التواصل الاجتماعي</span>
                        <div style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
                            {formData.socials.map((social, index) => (
                                <div key={index} style={{ display: "flex", gap: 12, alignItems: "center" }}>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            value={social.url}
                                            onChange={(e) => handleSocialChange(index, e.target.value)}
                                            placeholder="الرابط"
                                            style={{ flex: 2 }}
                                        />
                                    ) : (
                                        <span className="value">{social.url}</span>
                                    )}
                                    {isEditing && (
                                        <button className="btn outline demo-err" onClick={() => removeSocial(index)}>
                                            حذف
                                        </button>
                                    )}
                                </div>
                            ))}
                            {isEditing && (
                                <button style={{ width: 250 }} className="btn outline demo" onClick={addSocial}>
                                    إضافة حساب
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="profile-actions">
                    {isEditing ? (
                        <div style={{ display: "flex", gap: 10 }}>
                            <button className="btn filled" onClick={handleSave} disabled={isLoading}>
                                {isLoading ? "جارٍ الحفظ..." : "حفظ"}
                            </button>
                            <button className="btn outline" onClick={() => setIsEditing(false)}>
                                إلغاء
                            </button>
                        </div>
                    ) : (
                        <button className="btn filled" onClick={() => setIsEditing(true)}>
                            تعديل
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
